import React, { useState, useEffect } from 'react';
import { 
  Card, 
  PageTitle, 
  Spinner, 
  EmptyState 
} from '../components';
import { 
  FaChartLine, 
  FaExclamationCircle, 
  FaCalendarAlt 
} from 'react-icons/fa';
import api from '../services/api';

const Analytics = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    changesOverTime: [],
    siteStats: [],
    topChanges: []
  });
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('7days'); // 7days, 30days, 90days

  // 获取统计数据
  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setLoading(true);
        
        // 获取统计数据
        const response = await api.get(`/api/analytics?timeRange=${timeRange}`);
        setStats(response.data || {
          changesOverTime: [],
          siteStats: [],
          topChanges: []
        });
      } catch (err) {
        console.error('获取统计数据失败', err);
        setError('获取统计数据失败，请刷新页面重试');
      } finally {
        setLoading(false);
      }
    };
    
    fetchAnalytics();
  }, [timeRange]);

  // 如果正在加载，显示加载中状态
  if (loading) {
    return <Spinner fullPage text="加载统计数据..." />;
  }

  return (
    <div className="analytics-page">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <PageTitle 
          title="数据统计" 
          subtitle="查看网站监控数据和变更趋势"
          icon={<FaChartLine />}
        />
        
        <div className="time-range-selector">
          <div className="btn-group" role="group">
            <button 
              type="button" 
              className={`btn ${timeRange === '7days' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setTimeRange('7days')}
            >
              7天
            </button>
            <button 
              type="button" 
              className={`btn ${timeRange === '30days' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setTimeRange('30days')}
            >
              30天
            </button>
            <button 
              type="button" 
              className={`btn ${timeRange === '90days' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setTimeRange('90days')}
            >
              90天
            </button>
          </div>
        </div>
      </div>
      
      {/* 错误提示 */}
      {error && (
        <div className="alert alert-danger mb-4" role="alert">
          <FaExclamationCircle className="me-2" />
          {error}
        </div>
      )}
      
      {/* 变更趋势图 */}
      <Card 
        title="变更趋势" 
        className="mb-4"
        icon={<FaChartLine className="text-primary" />}
      >
        {stats.changesOverTime.length > 0 ? (
          <div className="chart-container" style={{ height: '300px' }}>
            {/* 此处应引入图表库，如Chart.js或Recharts来显示趋势图 */}
            <div className="text-center p-5 text-muted">
              <p>此处将显示变更趋势图表</p>
              <p>根据选择的时间范围：{timeRange === '7days' ? '最近7天' : timeRange === '30days' ? '最近30天' : '最近90天'}</p>
              <p>
                图表数据: {JSON.stringify(stats.changesOverTime).slice(0, 100)}...
              </p>
            </div>
          </div>
        ) : (
          <EmptyState 
            type="no-data"
            title="暂无趋势数据"
            message="所选时间范围内没有变更数据，请尝试选择更长的时间范围。"
          />
        )}
      </Card>
      
      {/* 站点统计卡片 */}
      <div className="row mb-4">
        <div className="col-12">
          <Card 
            title="站点变更统计" 
            className="mb-4"
            icon={<FaCalendarAlt className="text-primary" />}
          >
            {stats.siteStats.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-hover align-middle">
                  <thead>
                    <tr>
                      <th>站点名称</th>
                      <th>监控页面数</th>
                      <th>变更总数</th>
                      <th>重要变更</th>
                      <th>最后检测</th>
                      <th>活跃度</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.siteStats.map((site, index) => (
                      <tr key={site.siteId || index}>
                        <td>{site.siteName}</td>
                        <td>{site.pageCount}</td>
                        <td>{site.changeCount}</td>
                        <td>
                          <span className="badge bg-danger">
                            {site.importantChangeCount || 0}
                          </span>
                        </td>
                        <td>{new Date(site.lastChecked).toLocaleString()}</td>
                        <td>
                          <div className="progress" style={{ height: '6px' }}>
                            <div 
                              className={`progress-bar bg-${site.activityLevel > 66 ? 'success' : site.activityLevel > 33 ? 'warning' : 'danger'}`}
                              style={{ width: `${site.activityLevel}%` }}
                            ></div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <EmptyState 
                type="no-data"
                title="暂无站点数据"
                message="所选时间范围内没有站点统计数据。"
              />
            )}
          </Card>
        </div>
      </div>
      
      {/* 重要变更卡片 */}
      <div className="row">
        <div className="col-12">
          <Card 
            title="重要变更排行" 
            icon={<FaExclamationCircle className="text-danger" />}
          >
            {stats.topChanges.length > 0 ? (
              <div className="list-group list-group-flush">
                {stats.topChanges.map((change, index) => (
                  <div 
                    key={change.changeId || index}
                    className="list-group-item list-group-item-action p-3"
                  >
                    <div className="d-flex align-items-start">
                      <div className="rounded-circle bg-danger text-white d-flex align-items-center justify-content-center me-3" style={{ width: 28, height: 28 }}>
                        {index + 1}
                      </div>
                      <div className="flex-grow-1">
                        <h6 className="mb-1">{change.siteName} - {change.pageTitle || change.pageUrl}</h6>
                        <p className="text-muted mb-0 small">
                          重要性：{change.importance} · 变更幅度：{change.changeRate}% · 时间：{new Date(change.createdAt).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState 
                type="empty"
                title="暂无重要变更"
                message="所选时间范围内没有重要变更记录。"
              />
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Analytics; 